/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { TWidget } from 'widgets/Widget';

declare global {
    interface Window {
        optimizely: {
            push: (event) => any;
            initialized: boolean;
        };
        optimizelyEventsQueue: Array<string>;
        tauData: {
            currentSiteId: string;
            currentSiteLocale: string;
        };
    }
}

const SERVICE_CHANGE_EVENT = 'consentServiceChange';
const CONSENT_SERVICE_REQUEST_EVENT = 'consentServiceRequest';
const SERVICE_OPTIMIZELY = 'Optimizely';

/**
 * @description Base OptimizelyMeta implementation
 * @param Widget Base widget for extending
 * @returns OptimizelyMeta class
 */
function OptimizelyMetaClassCreator(Widget: TWidget) {
    class OptimizelyMeta extends Widget {
        optimizely?: any;

        initialized: boolean = false;

        isConsentGiven: boolean = false;

        analyticsEventLabel: string = 'ga4_event';

        analyticsEventName: string = 'opti_experiment';

        analyticsExpType: string = '';

        eventsBuffer: any = [];

        EVENTS = {
            PDP: 'pdp_visit',
            ADD_TO_CART: ''
        };

        init() {
            super.init();
            this.initInstanceWithDelay();

            this.eventBus().on('product.added.to.cart', 'productAddedToCart');
            this.eventBus().on('product.set.added.to.cart', 'productAddedToCart');
            this.eventBus().on(SERVICE_CHANGE_EVENT, 'processServiceChange');
            this.eventBus().emit(CONSENT_SERVICE_REQUEST_EVENT, SERVICE_OPTIMIZELY);
        }

        initInstance(): void | Promise<void> {
            /* stub */
        }

        /**
         * Calls implementation-specific initialization on document readyStateChange.
         */
        initInstanceWithDelay(): void {
            if (document.readyState === 'complete') {
                this.initInstance();
            } else {
                document.addEventListener('readystatechange', event => {
                    if ((event.target as Document).readyState === 'complete') {
                        setTimeout(() => this.initInstance(), 0);
                    }
                });
            }
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        trackEvent(_label?: any, _args?: any): Promise<void> | void {}

        /**
         * Calls analytics trackers for Experimentation decision event
         *
         * @param event - CustomEvent containing experimentation decision
         */
        trackAnalyticsEvent(event): void {
            const decision = event.detail || event.data;

            if (!decision) {
                return;
            }

            this.trackGoogleAnalyticsEvent(decision);
            this.trackContentSquareEvent(decision);
        }

        /**
         * Sends tracking decision event to Content Square
         *
         * @param decision - Optimizely decision
         */
        trackContentSquareEvent(decision): void {
            const CS_PREFIX = 'AB_OP_SS_';
            const CS_EVENT_KEY = `${CS_PREFIX + decision.flagKey}`;
            const trackingData = [
                'trackDynamicVariable',
                {
                    key: CS_EVENT_KEY,
                    value: decision.variationKey
                }
            ];

            window._uxa = window._uxa || [];
            window._uxa.push(trackingData);
        }

        /**
         * Returns true only for North American sites (US, CA).
         * The legacy opti_experiment DataLayer event is suppressed for all other regions (EMEA, APAC).
         */
        isNARegion(): boolean {
            const NA_SITE_IDS = ['US', 'CA'];

            return NA_SITE_IDS.includes(window.tauData?.currentSiteId);
        }

        /**
         * Pushes decision event to window.dataLayer for GTM.
         * Suppressed for non-NA regions (EMEA, APAC) — the legacy opti_experiment object
         * causes data duplication in those regions and has been replaced by the global
         * optimizely_holdback / optimizely_experience_variant_string variables (fired outside SFCC).
         *
         * @param decision - optimizely decision details
         */
        trackGoogleAnalyticsEvent(decision): void {
            if (!this.isNARegion()) {
                return;
            }

            const trackingData = {
                event: this.analyticsEventLabel,
                event_name: this.analyticsEventName,
                opti_experiment: {
                    feature: this.analyticsEventName,
                    exp_type: this.analyticsExpType,
                    exp_campaign: decision.flagKey || decision.campaign?.id,
                    exp_variation: decision.variationKey || decision.decision?.variationId
                }
            };

            const decisionDetailsString = JSON.stringify(trackingData);
            const isUnique = !this.eventsBuffer.some(event => JSON.stringify(event) === decisionDetailsString);

            if (isUnique) {
                window.dataLayer?.push(trackingData);
            }
        }

        /**
         * Handles changes to the Optimizely service consent state.
         *
         * @param type - The type of service whose consent state has changed.
         * @param accepted - Indicates whether consent has been given (true) or revoked (false).
         */
        processServiceChange(type, accepted) {
            if (type === SERVICE_OPTIMIZELY) {
                this.isConsentGiven = accepted;

                if (!this.optimizely) {
                    setTimeout(() => {
                        this.initInstanceWithDelay();
                    }, 700);
                }
            }
        }

        /**
         * Logs a warning message to the console.
         *
         * @param msg - The warning message to be logged.
         */
        logWarn(msg: string): void {
            // eslint-disable-next-line no-console
            console.log(msg);
        }

        /**
         * Event handler for tracking addToCart event
         *
         * @param cart - Cart object containing added product(s)
         */
        productAddedToCart(cart): void {
            const addedProducts = cart?.addedProducts && cart.addedProducts.length ? cart.addedProducts : [cart.addedProduct];

            if (!Array.isArray(addedProducts)) {
                return;
            }

            addedProducts.forEach(addedProduct => {
                const price = addedProduct?.price || {};

                if (price.list || price.sales) {
                    const { sales, list } = price;
                    const value = (sales?.value || list?.value || 0) * 100;

                    const data = {
                        revenue: value
                    };

                    this.trackEvent(this.EVENTS.ADD_TO_CART, data);
                }
            });
        }
    }

    return OptimizelyMeta;
}

export type TOptimizelyMeta = ReturnType<typeof OptimizelyMetaClassCreator>;

export type TOptimizelyMetaInstance = InstanceType<TOptimizelyMeta>;

export default OptimizelyMetaClassCreator;
